import { Tooltip } from '@material-ui/core';
import React, { useEffect, useReducer, useState } from 'react';
import CustomTextField from '../../../../../shared/form-controls/custom-text-field/Custom-Text-Field';
import InfoIcon from '@mui/icons-material/Info';
import CustomUpload from '../../../../../shared/form-controls/custom-upload/Custom-Upload';
import { ASSET_HOTELS, ASSET_TYPE_DOC } from '../../../../../constants/PropertyConst';
import { selectedGroupSelector } from '../../../../property/propertySlice';
import { useDispatch, useSelector } from 'react-redux';
import { formDataValidate } from '../../../../../utils/formUtils';
import './MenuEditDrawer.scss';
import CustomPopupModel from '../../../../../shared/custom-popup/Custom-Popup';
import { updateInRoomDiningMenu } from '../../../../../utils/ServicesAPIUtils';
import { fetchAllInRoomDiningMenu } from '../../../servicesSlice';
import { notificationManager } from '../../../../../utils/NotificationUtils';
import CustomFormView from '../../../../../shared/custom-form-view/Custom-Form-View';
import { IN_ROOM_DINING_MENUS_TAB } from '../../../../../constants/InRoomDiningConst';
import ImageUpload from '../../../../../shared/image-upload/ImageUpload';
import { isEmpty } from 'lodash';
import { getProcessedImages } from '../../../../../shared/image-upload/ImageProcessor';
import { decryptLocalStorage } from '../../../../../utils/authUtils';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../../../../utils/loaderSlice';
import { MENU_CONFIG_MAX_PDF_SIZE } from '../../../../../utils/Constants';

const MenuEditDrawer = (props) => {
  const { toggleDrawer, menu, hotelId, restaurantId, showLoader = () => {} } = props;
  const selectedGroup = useSelector(selectedGroupSelector);
  const [deletePopup, setDeletePopup] = useState(false);
  const [removeHandler, setRemoveHandler] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rawImages, setRawImages] = useState([]);
  const [isTabInValid, setIsTabInValid] = useState(true);

  const dispatch = useDispatch();
  const onCancel = (e) => {
    toggleDrawer('', false, '');
  };
  const onClickCancel = () => {
    setDeletePopup(false);
  };

  const [formData, setFormData] = useReducer((state, newState) => ({ ...state, ...newState }), {
    name: {
      value: '',
      fieldName: 'Menu Name',
      errors: {},
      validations: [],
      tab: 0,
    },
    code: {
      value: '',
      fieldName: 'Menu Code',
      errors: {},
      validations: ['validPureAlphaNumeric', 'maxLength'],
      maxLength: 8,
      tab: 0,
    },
    description: {
      value: '',
      fieldName: 'Description',
      errors: {},
      validations: [],
      tab: 0,
    },
    isActive: {
      value: true,
      fieldName: 'Active',
      errors: {},
      validations: [],
      tab: 0,
    },
    document: {
      value: { url: '', type: '', name: '' },
      fieldName: '',
      errors: {},
      validations: ['safeURL', 'safeDomain'],
      tab: 0,
    },
    images: {
      value: [],
      fieldName: 'Images',
      errors: {},
      validations: [],
      tab: 1,
    },
  });

  const handleActiveChange = (event) => {
    const currField = { ...formData.isActive };
    currField.value = event.target.checked;
    setFormData({ isActive: currField });
  };

  useEffect(() => {
    if (menu) {
      const currField = { ...formData };
      currField.name.value = menu.name;
      currField.code.value = menu.code;
      currField.description.value = menu.description;
      currField.isActive.value = menu.isActive;
      currField.document.value.url = menu.menuURL;
      currField.document.value.type = 'S3';
      currField.document.value.name = menu.name;
      currField.document.fieldName = 'Menu';
      currField.images.value = menu?.images;
      setFormData(currField);
    }
  }, [menu]);

  useEffect(() => {
    const currTabFromData = Object.fromEntries(Object.entries(formData));
    const isValid = formDataValidate(currTabFromData, false);
    setIsTabInValid(!isValid);
  }, [formData]);

  const onDocRemove = () => {
    const currField = { ...formData.document };
    currField.value.url = '';
    currField.value.type = '';
    currField.value.name = '';
    setFormData({ document: currField });
    setDeletePopup(false);
  };

  const modalPostion = () => {
    const top = 50;
    const left = 50;

    return {
      top: `${top}%`,
      left: `${left}%`,
      transform: `translate(-${top}%, -${left}%)`,
    };
  };

  const handleSubmit = async (e) => {
    try {
      dispatch(stepUpLoaderCount());
      setLoading(true);
      const selectedLanguage = decryptLocalStorage('lang');
      const language = selectedLanguage !== 'en' ? selectedLanguage : '';
      let payload = {};
      for (let field in formData) {
        payload[field] = formData[field].value;
      }
      payload.id = menu.id;
      payload.hotelId = hotelId;
      payload.restaurantId = restaurantId;
      payload.menuURL = formData.document.value.url;
      const images = await getProcessedImages(rawImages, selectedGroup, 'hotels', hotelId);
      payload.images = images;
      await updateInRoomDiningMenu(payload, language);
      dispatch(fetchAllInRoomDiningMenu(hotelId, restaurantId));
      setLoading(false);
      notificationManager('success', 'Menu updated successfully');
    } catch (error) {
      error?.graphQLErrors
        ? notificationManager('danger', error?.graphQLErrors?.[0]?.message)
        : notificationManager('danger', 'Error updating menu');
    } finally {
      dispatch(stepDownLoaderCount());
      setLoading(false);
      toggleDrawer('', false, '');
    }
  };

  const validate = (tabValue) => {
    const updatedFields = {};
    // If tabValue is given, filter out the form fields in current tab
    const currTabFromData = Object.fromEntries(
      Object.entries(formData).filter(([key, field]) => field.tab === tabValue),
    );
    const isValid = formDataValidate(currTabFromData, updatedFields);
    setFormData(updatedFields);
    return isValid;
  };

  const handleTabChange = (event, newValue) => {
    if (newValue < tabValue) {
      setTabValue(newValue);
    }
    if (validate(tabValue)) {
      setTabValue(newValue);
    }
  };

  const onClickNext = () => {
    if (validate(tabValue)) {
      setTabValue(tabValue + 1);
    }
  };

  const onImageListUpdated = (imgObjArr) => {
    if (imgObjArr?.length) {
      setRawImages(imgObjArr);
    } else if (rawImages?.length === 1) {
      setRawImages(imgObjArr);
      formData.images.value = imgObjArr;
      setFormData({ images: formData?.images });
    }
  };

  const onUploadedImageRemoval = (index) => {
    const images = [...formData.images];
    images.splice(index, 1);
    setFormData({ images });
  };
  return (
    <>
      <CustomFormView
        handleSubmit={handleSubmit}
        tabTitle={IN_ROOM_DINING_MENUS_TAB}
        onCancel={onCancel}
        tabValue={tabValue}
        maxtab={1}
        isTabInValid={
          isTabInValid ||
          (isEmpty(formData.document.value.url) &&
            isEmpty([...rawImages, ...formData.images.value]) &&
            tabValue === 1)
        }
        isUpdate={true}
        loading={loading}
        handleTabChange={handleTabChange}
        title={'Edit Menu'}
        onClickNext={onClickNext}
        titleClass='col-md-4'
        tabClass='col-md-4'
        cancelClass='col-md-4 '
      >
        {tabValue === 0 && (
          <div className='row px-3 w-100 d-flex mt-5 overflow-auto'>
            <h4 className='tab-title'>Details</h4>
            <div className='row'>
              <div className='col-md-6 mb-3 '>
                <CustomTextField
                  item={formData.name}
                  handleInputChange={(e) => {
                    setFormData({ name: e });
                  }}
                  maxLength='30'
                  multiline={true}
                  endAdornment={true}
                  characterCount={true}
                />
              </div>
              <div className='col-md-6 mb-3'>
                <CustomTextField
                  item={formData.code}
                  maxLength={8}
                  endAdornment={true}
                  characterCount={true}
                  handleInputChange={(e) => {
                    setFormData({ code: e });
                  }}
                />
              </div>
            </div>
            <div className='row'>
              <div className='col-md-12 mb-3'>
                <CustomTextField
                  item={formData.description}
                  handleInputChange={(e) => {
                    setFormData({ description: e });
                  }}
                  multiline={true}
                />
              </div>
            </div>
            {/*hide activate checkbox temporally  */}
            {/* <div className="col-md-5 ms-2">
              <div className="mt-3 d-flex align-items-center">
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        className="check-box"
                        checked={formData.isActive.value}
                        onChange={handleActiveChange}
                      />
                    }
                    label="Active"
                  />
                </FormGroup>
                <div>
                  <Tooltip
                    title="Deactivating this, hides it from Guest App"
                    placement="right"
                  >
                    <InfoIcon fontSize="small" className="ms-2" />
                  </Tooltip>
                </div>
              </div>
            </div> */}
            <div className='mt-5 mb-3 d-flex'>
              <h4 className='tab-title'>Document</h4>
              <div className='ms-2'>
                <Tooltip title='Document type: PDF, Maximum file size: 50MB' placement='right'>
                  <InfoIcon fontSize='small' className='ms-2' />
                </Tooltip>
              </div>
            </div>
            <div>
              <CustomUpload
                item={formData.document}
                hotelId={hotelId}
                groupId={selectedGroup}
                name={formData.name}
                radioDisable={true}
                disableName={true}
                setDeletePopup={setDeletePopup}
                setRemoveHandler={setRemoveHandler}
                setFormData={setFormData}
                additionalInput={{
                  type: ASSET_TYPE_DOC,
                  groupId: selectedGroup,
                  propertyType: ASSET_HOTELS,
                }}
                onUpload={(e) => {
                  setFormData({ document: e });
                }}
                isDocUpload={true}
                isMenuImages={formData.images.value}
                isCustomMenu={true}
                maxFileSize={MENU_CONFIG_MAX_PDF_SIZE}
              />
            </div>
          </div>
        )}
        {tabValue === 1 && (
          <div className='col-md-12 mb-3 form-scroll'>
            <ImageUpload
              uploadedImages={formData.images.value}
              imgList={rawImages}
              onImageListUpdated={onImageListUpdated}
              onUploadedImageRemoval={onUploadedImageRemoval}
              onImagesLoading={(status) => {
                setLoading(status);
              }}
              isMenuUrl={formData.document.value.url}
              enabledMediaTabs={['image']}
            />
          </div>
        )}
      </CustomFormView>
      {deletePopup && (
        <CustomPopupModel
          popUp={{
            status: deletePopup,
            title: 'Are you sure you want to remove this document?',
            description: 'Please reupload a new document and save',
            buttonName: 'Yes',
            cancel: 'No',
          }}
          style={{
            paddingLeft: '2rem',
            paddingRight: '2rem',
          }}
          onClickCancel={onClickCancel}
          onClick={onDocRemove}
          modalPostion={modalPostion}
        />
      )}
    </>
  );
};

export default MenuEditDrawer;
